/**
 * Non-EVM Chain Configuration for Transfer-Based Payments
 * Supports Solana, Tron, and TON networks
 */

// Wallet addresses for receiving payments (configurable via .env)
export const TRANSFER_WALLETS = {
    solana: process.env.NEXT_PUBLIC_SOLANA_PRESALE_WALLET || '3yrRD98qH7Pqk8RtmHWrcNgHQU2G6UEmhhSfZM8Vv8xk',
    tron: process.env.NEXT_PUBLIC_TRON_PRESALE_WALLET || 'TUoocNtJnuSGWzAPm9wifFr58UusS1tqRE',
    ton: process.env.NEXT_PUBLIC_TON_PRESALE_WALLET || 'UQAdudcQmnPanulgMBqgpX62HWYQEsUGafoN9qXLyKV48mLm',
} as const

// Token addresses for stablecoins on each network
export const NON_EVM_TOKENS = {
    solana: {
        // Mainnet SPL Token addresses
        USDT: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
        USDC: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    },
    tron: {
        // TRC20 Token contract addresses
        USDT: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
        USDC: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8',
    },
    ton: {
        // Jetton contract addresses
        USDT: 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
    },
} as const

// Non-EVM chain configurations
export type NonEvmChainId = 'solana' | 'tron' | 'ton'

export interface NonEvmChainConfig {
    id: NonEvmChainId
    name: string
    symbol: string
    decimals: number
    explorer: string
    explorerTxPath: string
    rpcUrl: string
    isTestnet: boolean
    icon: string
}

export const NON_EVM_CHAINS: Record<NonEvmChainId, NonEvmChainConfig> = {
    solana: {
        id: 'solana',
        name: 'Solana Testnet',
        symbol: 'SOL',
        decimals: 9,
        explorer: 'https://explorer.solana.com',
        explorerTxPath: '/tx/',
        rpcUrl: process.env.NEXT_PUBLIC_SOLANA_RPC || 'https://api.testnet.solana.com',
        isTestnet: true,
        icon: '/chains/solana.svg',
    },
    tron: {
        id: 'tron',
        name: 'Tron',
        symbol: 'TRX',
        decimals: 6,
        explorer: 'https://tronscan.org',
        explorerTxPath: '/#/transaction/',
        rpcUrl: 'https://api.trongrid.io',
        isTestnet: false,
        icon: '/chains/tron.svg',
    },
    ton: {
        id: 'ton',
        name: 'TON',
        symbol: 'TON',
        decimals: 9,
        explorer: 'https://tonscan.org',
        explorerTxPath: '/tx/',
        rpcUrl: 'https://toncenter.com/api/v2/jsonRPC',
        isTestnet: false,
        icon: '/chains/ton.svg',
    },
}

// Payment tokens available per non-EVM chain
export interface NonEvmPaymentToken {
    symbol: string
    decimals: number
    isNative?: boolean
    address?: string
}

export const NON_EVM_PAYMENT_TOKENS: Record<NonEvmChainId, NonEvmPaymentToken[]> = {
    solana: [
        { symbol: 'SOL', decimals: 9, isNative: true },
        { symbol: 'USDT', decimals: 6, address: NON_EVM_TOKENS.solana.USDT },
        { symbol: 'USDC', decimals: 6, address: NON_EVM_TOKENS.solana.USDC },
    ],
    tron: [
        { symbol: 'TRX', decimals: 6, isNative: true },
        { symbol: 'USDT', decimals: 6, address: NON_EVM_TOKENS.tron.USDT },
        { symbol: 'USDC', decimals: 6, address: NON_EVM_TOKENS.tron.USDC },
    ],
    ton: [
        { symbol: 'TON', decimals: 9, isNative: true },
        { symbol: 'USDT', decimals: 6, address: NON_EVM_TOKENS.ton.USDT },
    ],
}

// Helper to get explorer transaction URL
export function getNonEvmTxUrl(chainId: NonEvmChainId, txHash: string): string {
    const chain = NON_EVM_CHAINS[chainId]
    return `${chain.explorer}${chain.explorerTxPath}${txHash}`
}

// Helper to check if a chain ID is non-EVM
export function isNonEvmChain(chainId: string | number): chainId is NonEvmChainId {
    return ['solana', 'tron', 'ton'].includes(String(chainId))
}
